/* ez-cart.js — vanilla, instance-safe cart line behavior for the three ez-cart-* sections.
   Wires the qty steppers + remove links in ez-cart-lines to Shopify's /cart/change.js, then
   re-renders the affected sections via the Section Rendering API so the ledger, header count,
   and dark summary rail all stay in sync without a full page reload. No framework, no globals
   beyond one guarded init; every handler is bound per-element and re-bound after each re-render. */
(function () {
  var MONEY = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{amount}}';

  // Which sections to ask the Section Rendering API to re-render on every cart mutation.
  // Read from the data-attributes each ez-cart-* section stamps on itself, deduped.
  function sectionIds() {
    var ids = {};
    document.querySelectorAll('[data-ez-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-ez-cart-section');
      if (id) ids[id] = true;
    });
    return Object.keys(ids);
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // Change one line by its 1-based line index (stable even when multiple lines share nothing else).
  function changeLine(line, quantity, sections) {
    return post(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
      line: line,
      quantity: quantity,
      sections: sections,
    });
  }

  // Batch-set a map of { lineItemKey: quantity } in ONE request — used by "Remove build" (a whole
  // _build group at once) and "Clear cart" (every line). Keys are stable across the removal; line
  // indices are not. update.js honours the same Section Rendering `sections` param as change.js.
  function updateByKeys(updates) {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return post(root + 'cart/update.js', { updates: updates, sections: sectionIds() });
  }

  // Swap the innerHTML of each live section wrapper with the freshly rendered markup.
  function applySections(data) {
    if (!data || !data.sections) return;
    Object.keys(data.sections).forEach(function (id) {
      var live = document.getElementById('shopify-section-' + id);
      if (!live) return;
      var doc = new DOMParser().parseFromString(data.sections[id], 'text/html');
      var fresh = doc.getElementById('shopify-section-' + id);
      if (fresh) live.innerHTML = fresh.innerHTML;
    });
    bindAll();
  }

  function setBusy(row, busy) {
    if (!row) return;
    row.setAttribute('aria-busy', busy ? 'true' : 'false');
    row.style.opacity = busy ? '0.5' : '';
    row.querySelectorAll('button, a').forEach(function (b) { b.style.pointerEvents = busy ? 'none' : ''; });
  }

  function mutate(line, quantity, row) {
    setBusy(row, true);
    changeLine(line, quantity, sectionIds())
      .then(applySections)
      .catch(function () { setBusy(row, false); });
  }

  // "Remove build": zero every line-item key inside one .ez-cart-lines__build group in one request.
  function removeBuild(buildEl) {
    if (!buildEl) return;
    var updates = {};
    buildEl.querySelectorAll('[data-ez-key]').forEach(function (el) { updates[el.getAttribute('data-ez-key')] = 0; });
    if (!Object.keys(updates).length) return;
    buildEl.style.opacity = '0.5';
    buildEl.style.pointerEvents = 'none';
    updateByKeys(updates).then(applySections).catch(function () { buildEl.style.opacity = ''; buildEl.style.pointerEvents = ''; });
  }

  // "Clear cart": zero every line in the ledger. Emptying re-renders the header's empty state.
  function clearCart() {
    var updates = {};
    document.querySelectorAll('.ez-cart-lines__ledger [data-ez-key]').forEach(function (el) { updates[el.getAttribute('data-ez-key')] = 0; });
    if (!Object.keys(updates).length) return;
    updateByKeys(updates).then(applySections);
  }

  function bindBuildRemove(btn) {
    if (btn.__ezBound) return;
    btn.__ezBound = true;
    btn.addEventListener('click', function () {
      var buildEl = btn.closest('.ez-cart-lines__build');
      var label = btn.getAttribute('data-ez-build-label') || 'this';
      window.ezConfirm({
        title: 'Remove this build?',
        message: 'This removes your ' + label + ' build and everything in it: the kit plus its snaps and studs.',
        confirmLabel: 'Remove build',
        danger: true,
      }).then(function (ok) { if (ok) removeBuild(buildEl); });
    });
  }

  function bindClear(btn) {
    if (btn.__ezBound) return;
    btn.__ezBound = true;
    btn.addEventListener('click', function () {
      window.ezConfirm({
        title: 'Clear your cart?',
        message: 'This removes everything from your cart. This can’t be undone.',
        confirmLabel: 'Clear cart',
        danger: true,
      }).then(function (ok) { if (ok) clearCart(); });
    });
  }

  function bindRow(row) {
    if (row.__ezBound) return;
    row.__ezBound = true;
    var line = parseInt(row.getAttribute('data-ez-line'), 10);
    var qtyEl = row.querySelector('[data-ez-qty-value]');
    var qty = parseInt(qtyEl ? qtyEl.getAttribute('data-ez-qty-value') : '1', 10) || 1;

    var dec = row.querySelector('[data-ez-qty="down"]');
    var inc = row.querySelector('[data-ez-qty="up"]');
    var rm = row.querySelector('[data-ez-remove]');

    if (dec) dec.addEventListener('click', function () { if (qty > 1) mutate(line, qty - 1, row); });
    if (inc) inc.addEventListener('click', function () { mutate(line, qty + 1, row); });
    if (rm) rm.addEventListener('click', function (e) { e.preventDefault(); mutate(line, 0, row); });
  }

  // Cross-sell row "Add to cart": add one of the variant, re-render the cart sections in place (the added
  // product drops out of the row server-side, which skips products already in the cart).
  function bindXsAdd(btn) {
    if (btn.dataset.ezBound) return;
    btn.dataset.ezBound = '1';
    btn.addEventListener('click', function () {
      var id = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!id) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      post(root + 'cart/add.js', { items: [{ id: id, quantity: 1 }], sections: sectionIds() })
        .then(function (data) {
          if (data && data.status && data.status >= 400) throw new Error(data.description || 'add failed');
          applySections(data);
          document.dispatchEvent(new CustomEvent('ez:cart:updated'));
        })
        .catch(function () { btn.disabled = false; btn.textContent = 'Try again'; setTimeout(function () { btn.textContent = label; }, 2500); });
    });
  }

  function bindAll() {
    document.querySelectorAll('[data-ez-xs-add]').forEach(bindXsAdd);
    document.querySelectorAll('[data-ez-line]').forEach(bindRow);
    document.querySelectorAll('[data-ez-remove-build]').forEach(bindBuildRemove);
    document.querySelectorAll('[data-ez-clear-cart]').forEach(bindClear);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
})();
